import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, Shield, PiggyBank, CalendarClock } from 'lucide-react';
import { formatCurrency, getInsuranceStatus, getExpiryDate } from './InsurancePolicies';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { usePagination } from '@/hooks/usePagination';
import { ListPagination } from '@/components/ListPagination';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { businessToday } from '@/lib/format';
import { driverProfileName } from '@/lib/driverProfile';

export default function InsuranceAnalytics() {
  const [monthRange, setMonthRange] = useState({
    from: startOfMonth(subMonths(businessToday(), 11)),
    to: endOfMonth(businessToday()),
  });

  const fromDate = format(monthRange.from, 'yyyy-MM-dd');
  const toDate = format(monthRange.to, 'yyyy-MM-dd');

  // Fetch summary statistics
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['insurance-analytics-summary', fromDate, toDate],
    queryFn: async () => {
      // Get all insurance premiums with amounts from transactions
      const { data: premiums, error: premiumsError } = await supabase
        .from('insurance_premiums')
        .select('id, insurance_id, transactions(amount, transaction_at)')
        .gte('created_at', fromDate)
        .lte('created_at', toDate + 'T23:59:59');
      if (premiumsError) throw premiumsError;

      // Get all insurance deposits
      const { data: deposits, error: depositsError } = await supabase
        .from('insurance_loan_deposits')
        .select('id, transactions(amount, transaction_at)')
        .gte('created_at', fromDate)
        .lte('created_at', toDate + 'T23:59:59');
      if (depositsError) throw depositsError;

      // Get insurance claims
      const { data: claims, error: claimsError } = await supabase
        .from('insurance_claims')
        .select('id, transactions(amount, transaction_at)')
        .gte('created_at', fromDate)
        .lte('created_at', toDate + 'T23:59:59');
      if (claimsError) throw claimsError;

      // Calculate totals
      const totalPremiums = premiums?.reduce((sum, p) => sum + (p.transactions?.amount || 0), 0) || 0;
      const totalDeposits = deposits?.reduce((sum, d) => sum + (d.transactions?.amount || 0), 0) || 0;
      const totalClaims = claims?.reduce((sum, c) => sum + (c.transactions?.amount || 0), 0) || 0;

      const totalCollected = totalPremiums + totalDeposits;
      const fundBalance = totalCollected - totalClaims;

      return {
        totalPolicies: new Set(premiums?.map(p => p.insurance_id)).size,
        totalPremiums,
        totalDeposits,
        totalCollected,
        totalClaims,
        fundBalance,
        claimsCount: claims?.length || 0,
      };
    },
  });

  // Fetch monthly trend data
  const { data: monthlyData, isLoading: monthlyLoading } = useQuery({
    queryKey: ['insurance-analytics-monthly', fromDate, toDate],
    queryFn: async () => {
      const { data: premiums, error: premiumsError } = await supabase
        .from('insurance_premiums')
        .select('transactions(amount, transaction_at)')
        .gte('created_at', fromDate)
        .lte('created_at', toDate + 'T23:59:59');
      if (premiumsError) throw premiumsError;

      const { data: claims, error: claimsError } = await supabase
        .from('insurance_claims')
        .select('transactions(amount, transaction_at)')
        .gte('created_at', fromDate)
        .lte('created_at', toDate + 'T23:59:59');
      if (claimsError) throw claimsError;

      // Generate month keys
      const monthlyMap = new Map<string, { premiums: number; claims: number }>();
      const current = new Date(monthRange.from.getFullYear(), monthRange.from.getMonth(), 1);
      while (current <= monthRange.to) {
        const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        monthlyMap.set(key, { premiums: 0, claims: 0 });
        current.setMonth(current.getMonth() + 1);
      }

      premiums?.forEach(p => {
        const txDate = p.transactions?.transaction_at;
        if (!txDate) return;
        // The stored Ghana month, as the sibling Analytics files bucket it. Reading local parts off
        // the instant put a late-evening month-end payment into the viewer's next (or previous) bar.
        const key = txDate.substring(0, 7);
        if (monthlyMap.has(key)) {
          const data = monthlyMap.get(key)!;
          data.premiums += p.transactions?.amount || 0;
        }
      });

      claims?.forEach(c => {
        const txDate = c.transactions?.transaction_at;
        if (!txDate) return;
        const key = txDate.substring(0, 7);
        if (monthlyMap.has(key)) {
          const data = monthlyMap.get(key)!;
          data.claims += c.transactions?.amount || 0;
        }
      });

      return Array.from(monthlyMap.entries()).map(([month, data]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
        premiums: data.premiums,
        claims: data.claims,
        net: data.premiums - data.claims,
      }));
    },
  });

  // Fetch upcoming renewals
  const { data: renewalsData, isLoading: renewalsLoading } = useQuery({
    queryKey: ['insurance-analytics-renewals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('insurances')
        .select(`
          id, starts_at, expires_at, vehicle_id,
          vehicles(id, make, model, vehicle_registrations(number)),
          insurance_contracts(
            contracts(drivers(profiles(id, admin_id, first_name, last_name)))
          ),
          insurance_premiums(transactions(amount))
        `)
        .not('starts_at', 'is', null)
        .order('starts_at', { ascending: false });
      if (error) throw error;

      // Filter to policies requiring attention (expiring soon or expired)
      return data?.filter(ins => {
        const status = getInsuranceStatus(ins.expires_at);
        return status === 'expiring_soon' || status === 'expired';
      }).map(ins => ({
        ...ins,
        contracts: (ins as any).insurance_contracts?.[0]?.contracts || null,
      })) || [];
    },
  });

  const {
    paginatedData: paginatedRenewals,
    currentPage: renewalsPage,
    totalPages: renewalsTotalPages,
    totalItems: renewalsTotalItems,
    goToPage: goToRenewalsPage,
  } = usePagination({ data: renewalsData, itemsPerPage: 10 });

  const pieData = summaryData ? [
    { name: 'Premiums Collected', value: summaryData.totalPremiums, color: 'hsl(var(--chart-1))' },
    { name: 'Deposits Collected', value: summaryData.totalDeposits, color: 'hsl(var(--chart-3))' },
    { name: 'Claims Paid', value: summaryData.totalClaims, color: 'hsl(var(--chart-2))' },
  ] : [];

  if (summaryLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Month Range Picker */}
      <div className="flex justify-end">
        <MonthRangePicker
          monthRange={monthRange}
          onMonthRangeChange={setMonthRange}
        />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Collected</CardTitle>
            <PiggyBank className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summaryData?.totalCollected || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {summaryData?.totalPolicies || 0} policies
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Claims Paid</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(summaryData?.totalClaims || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {summaryData?.claimsCount || 0} claims
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fund Balance</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(summaryData?.fundBalance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(summaryData?.fundBalance || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Collected - Claims
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Premium</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summaryData?.totalPolicies ? summaryData.totalPremiums / summaryData.totalPolicies : 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Per policy
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Monthly Trend Chart */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle>Monthly Fund Performance</CardTitle>
            <CardDescription>Premiums collected vs claims paid</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyLoading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                  />
                  <Area type="monotone" dataKey="premiums" stackId="1" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.6} name="Premiums" />
                  <Area type="monotone" dataKey="claims" stackId="2" stroke="hsl(var(--chart-2))" fill="hsl(var(--chart-2))" fillOpacity={0.6} name="Claims" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Fund Distribution Pie Chart */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle>Fund Distribution</CardTitle>
            <CardDescription>Breakdown of collections and claims</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Renewals */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Policies Requiring Attention
          </CardTitle>
          <CardDescription>Expired or expiring soon policies</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {renewalsLoading ? (
            <Skeleton className="h-48" />
          ) : renewalsData && renewalsData.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Premium</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRenewals.map((ins) => {
                    const status = getInsuranceStatus(ins.expires_at);
                    const vehicleReg = ins.vehicles?.vehicle_registrations;
                    const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number;
                    // The driver's own row and never an admin's test profile — see `driverProfileOf`.
                    const driverName = driverProfileName(ins.contracts?.drivers?.profiles) ?? 'N/A';
                    const premiumData = ins.insurance_premiums;
                    const premium = Array.isArray(premiumData)
                      ? premiumData.reduce((sum, p) => sum + (p.transactions?.amount || 0), 0)
                      : (premiumData?.transactions?.amount || 0);

                    return (
                      <TableRow key={ins.id}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{regNumber || 'Unregistered'}</span>
                            <p className="text-xs text-muted-foreground">
                              {ins.vehicles?.make} {ins.vehicles?.model}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{driverName}</TableCell>
                        <TableCell>{formatCurrency(premium)}</TableCell>
                        <TableCell>{getExpiryDate(ins.expires_at)}</TableCell>
                        <TableCell>
                          {status === 'expired' ? (
                            <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">Expired</Badge>
                          ) : (
                            <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700">Expiring Soon</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <ListPagination
                currentPage={renewalsPage}
                totalPages={renewalsTotalPages}
                onPageChange={goToRenewalsPage}
                totalItems={renewalsTotalItems}
              />
            </>
          ) : (
            <p className="text-center text-muted-foreground py-8">No policies requiring immediate attention</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
